import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["transitionProps"],
  _excluded2 = ["modal", "sandboxed", "children"],
  _excluded3 = ["align", "animate", "children", "currentPanel", "modal", "onCloseComplete", "onCloseStart", "onOpenComplete", "onOpenStart", "onPreviousClick", "panelOrder"];
import { Children, cloneElement, isValidElement, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import styled from 'foundations-theming/utils';
import { PanelNavigatorContext } from '../../context/PanelNavigatorContext';
import memoizeOne from 'react-utils/memoizeOne';
import { PanelTransition } from '../PanelTransition/PanelTransition';
import PanelNavigatorTransition from '../PanelNavigatorTransition/PanelNavigatorTransition';
import { SandboxContext } from '../../utils/SandboxContext';
import { DialogBackButton } from '../DialogBackButton';
import { ModalPanel } from '../ModalPanel';
import { Layer } from '../Layer';
import { getPanelTransitionProps } from './panelUtils';
import { rgba } from '../../utils/color';
import { PanelNavigatorProvider } from './PanelNavigatorProvider';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { PANEL_NAVIGATOR_TRANSITION_TIMING } from '../../../constants/times';
import { jsx as _jsx } from "react/jsx-runtime";
const transitionTime = parseInt(PANEL_NAVIGATOR_TRANSITION_TIMING, 10);
const NonModalLayer = props => {
  return /*#__PURE__*/_jsx(Layer, Object.assign({}, props, {
    Transition: PanelTransition
  }));
};
const ModalLayer = props => {
  const rest = _objectWithoutPropertiesLoose(props, _excluded);
  return /*#__PURE__*/_jsx(ModalPanel, Object.assign({
    width: "auto"
  }, rest));
};
const StyledLayer = props => {
  const {
      modal,
      sandboxed,
      children
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded2);
  if (sandboxed) {
    return /*#__PURE__*/_jsx("div", {
      style: {
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0
      },
      children: children
    });
  }
  const LayerComponent = modal ? ModalLayer : NonModalLayer;
  return /*#__PURE__*/_jsx(LayerComponent, Object.assign({}, rest, {
    children: children
  }));
};
const StyledPanelNavigator = styled.div.withConfig({
  displayName: "PanelNavigator__StyledPanelNavigator",
  componentId: "vlstkb-0"
})(({
  theme,
  $align,
  $cssStyles
}) => {
  return Object.assign({
    height: '100%',
    overflowX: 'hidden',
    overflowY: 'auto',
    boxShadow: `${$align === 'left' ? 3 : -3}px 0 8px 0 ${rgba(theme.color['fill-accent-neutral-default'], 0.21)}`
  }, $cssStyles);
});
export const PanelNavigator = _ref => {
  let {
      align = 'right',
      animate = true,
      children,
      currentPanel,
      modal = false,
      onCloseComplete,
      onCloseStart,
      onOpenComplete,
      onOpenStart,
      onPreviousClick,
      panelOrder
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded3);
  const [animateForward, setAnimateForward] = useState(true);
  const [prevCurrentPanel, setPrevCurrentPanel] = useState(currentPanel);
  const {
    sandboxed
  } = useContext(SandboxContext);
  const renderPreviousButton = useCallback(panelKey => {
    var _props;
    const isRootPanel = panelOrder ? panelOrder.indexOf(panelKey) === 0 : /*#__PURE__*/isValidElement(Children.toArray(children)[0]) && ((_props = Children.toArray(children)[0].props) === null || _props === void 0 ? void 0 : _props.panelKey) === panelKey;
    if (isRootPanel) return null;
    return /*#__PURE__*/_jsx(DialogBackButton, {
      onClick: onPreviousClick
    });
  }, [children, onPreviousClick, panelOrder]);
  const panelNavigatorCtxValue = useMemo(() => ({
    getCurrentPanel: () => currentPanel !== null && currentPanel !== void 0 ? currentPanel : '',
    renderNavigation: renderPreviousButton(currentPanel !== null && currentPanel !== void 0 ? currentPanel : '')
  }), [currentPanel, renderPreviousButton]);
  const panelStylesOverrides = {
    boxShadow: 'none !important',
    flexShrink: 0,
    position: 'relative'
  };
  const getTransitionProps = memoizeOne(getPanelTransitionProps);
  useEffect(() => {
    if (currentPanel !== prevCurrentPanel) {
      let nextPanelIndex = -1;
      let currentPanelIndex = -1;
      if (panelOrder) {
        nextPanelIndex = panelOrder.indexOf(currentPanel !== null && currentPanel !== void 0 ? currentPanel : '');
        currentPanelIndex = panelOrder.indexOf(prevCurrentPanel !== null && prevCurrentPanel !== void 0 ? prevCurrentPanel : '');
      }
      if (nextPanelIndex === -1 || currentPanelIndex === -1) {
        Children.forEach(children, (child, i) => {
          if (child.props.panelKey === currentPanel) nextPanelIndex = i;
          if (child.props.panelKey === prevCurrentPanel) currentPanelIndex = i;
        });
      }
      const shouldAnimateForward = nextPanelIndex > currentPanelIndex;
      if (shouldAnimateForward !== animateForward) {
        setAnimateForward(shouldAnimateForward);
      }
      setPrevCurrentPanel(currentPanel);
    }
  }, [currentPanel, prevCurrentPanel, panelOrder, children, animateForward]);
  const getCurrentPanel = () => {
    const panel = Children.toArray(children).find(child => child.props.panelKey === currentPanel);
    if (panel) {
      return /*#__PURE__*/cloneElement(panel, {
        _dontMakeNewLayer: true,
        key: panel.props.panelKey,
        align
      });
    }
    return panel;
  };
  const currentPanelElement = getCurrentPanel();
  const {
    domProps,
    tokenProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  return /*#__PURE__*/_jsx(PanelNavigatorProvider, {
    panelStylesOverrides: panelStylesOverrides,
    children: /*#__PURE__*/_jsx(PanelNavigatorContext.Provider, {
      value: panelNavigatorCtxValue,
      children: /*#__PURE__*/_jsx(StyledLayer, Object.assign({
        "data-layer-for": "StyledPanelNavigator",
        modal: modal,
        sandboxed: sandboxed,
        transitionProps: getTransitionProps(onOpenStart, onOpenComplete, onCloseStart, onCloseComplete, align, animate)
      }, rest, {
        children: /*#__PURE__*/_jsx(StyledPanelNavigator, Object.assign({
          $cssStyles: cssStyles,
          $align: align
        }, domProps, {
          children: /*#__PURE__*/_jsx(PanelNavigatorTransition, {
            duration: transitionTime,
            reverse: !animateForward,
            children: currentPanelElement
          })
        }))
      }))
    })
  });
};